/**
 * Lenavio Talent Map — per-employee skills profile.
 *
 * Renders:
 *   * Radar chart (current + role-required overlay)
 *   * Editable skill table (proficiency stepper per skill)
 *   * AI Assess button (suggests skills + levels)
 *   * Development plans for the employee
 *
 * Routes are mounted at ``/talent/employees/{id}``. Auth is
 * MANAGER+ — employees see their own profile via a separate
 * "self" route in a later build; this page is the HR/manager
 * surface.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  aiAssessEmployee,
  createDevelopmentPlan,
  createSkill,
  getEmployeeProfile,
  listEmployeePlans,
  listSkills,
  upsertEmployeeSkills,
  type AIAssessSuggestion,
  type DevelopmentPlan,
  type EmployeeSkillProfile,
  type ProficiencyLevel,
  type Skill,
  type SkillCategory,
} from "@/api/talent";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { SkillRadar, type RadarPoint } from "@/components/talent/SkillRadar";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

const LEVEL_LABELS: Record<number, string> = {
  0: "—",
  1: "Beginner",
  2: "Intermediate",
  3: "Advanced",
  4: "Expert",
};

interface DraftSkill {
  skill_id: string;
  skill_name: string;
  skill_category: SkillCategory;
  proficiency_level: number;
}

export function EmployeeSkillsPage() {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();

  const [profile, setProfile] = useState<EmployeeSkillProfile | null>(null);
  const [library, setLibrary] = useState<Skill[]>([]);
  const [draft, setDraft] = useState<Record<string, DraftSkill>>({});
  const [plans, setPlans] = useState<DevelopmentPlan[]>([]);
  const [saving, setSaving] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  // Bootstrap.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([getEmployeeProfile(id), listSkills(), listEmployeePlans(id)])
      .then(([p, lib, pl]) => {
        if (cancelled) return;
        setProfile(p);
        setLibrary(lib);
        setPlans(pl);
        // Seed the draft from the persisted skills.
        const map: Record<string, DraftSkill> = {};
        for (const es of p.skills) {
          map[es.skill_id] = {
            skill_id: es.skill_id,
            skill_name: es.skill_name || "Skill",
            skill_category:
              (es.skill_category as SkillCategory) || "technical",
            proficiency_level: es.proficiency_level,
          };
        }
        setDraft(map);
      })
      .catch(() => addToast("error", "Could not load profile."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const requirementsBySkillId = useMemo(() => {
    const m = new Map<string, number>();
    if (!profile) return m;
    for (const r of profile.requirements) {
      m.set(r.skill_id, r.required_level);
    }
    return m;
  }, [profile]);

  // Build radar input — the employee's tracked skills + any
  // role-required skill they don't yet have (so the gap shows
  // in the chart).
  const radarPoints: RadarPoint[] = useMemo(() => {
    if (!profile) return [];
    const tracked = Object.values(draft).map((d) => ({
      label: d.skill_name,
      current: d.proficiency_level,
      required: requirementsBySkillId.get(d.skill_id),
    }));
    const trackedIds = new Set(Object.keys(draft));
    for (const r of profile.requirements) {
      if (!trackedIds.has(r.skill_id)) {
        tracked.push({
          label: r.skill_name,
          current: 0,
          required: r.required_level,
        });
      }
    }
    return tracked;
  }, [profile, draft, requirementsBySkillId]);

  const updateLevel = (skillId: string, level: number) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (level === 0) {
        delete next[skillId];
      } else if (next[skillId]) {
        next[skillId] = { ...next[skillId], proficiency_level: level };
      }
      return next;
    });
  };

  const addToDraft = (s: Skill, level: number = 2) => {
    setDraft((prev) => ({
      ...prev,
      [s.id]: {
        skill_id: s.id,
        skill_name: s.name,
        skill_category: s.category,
        proficiency_level: level,
      },
    }));
  };

  const removeFromDraft = (skillId: string) => {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[skillId];
      return next;
    });
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await upsertEmployeeSkills(id, {
        skills: Object.values(draft).map((d) => ({
          skill_id: d.skill_id,
          proficiency_level: d.proficiency_level as ProficiencyLevel,
        })),
        replace_all: true,
      });
      addToast("success", "Profile saved.");
      // Refetch + re-seed the draft from persisted state to catch
      // any auto-closed plans + verify levels server-side.
      const [p, pl] = await Promise.all([
        getEmployeeProfile(id),
        listEmployeePlans(id),
      ]);
      setProfile(p);
      setPlans(pl);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not save profile.";
      addToast("error", detail);
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded bg-border/30 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title={profile.employee_name}
        subtitle={profile.role_title || "Skills profile"}
        actions={
          <div className="flex gap-2">
            <Link
              to="/talent"
              className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Talent Map
            </Link>
            <Button variant="secondary" onClick={() => setAiOpen(true)}>
              <Sparkles className="h-4 w-4" />
              AI Assess
            </Button>
            <Button onClick={handleSave} isLoading={saving}>
              Save profile
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
            Skills radar
          </h3>
          <SkillRadar points={radarPoints} />
          <p className="text-[11px] text-text-muted text-center mt-3">
            Filled = current · dashed outline = role expectation
          </p>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
            Role expectations
          </h3>
          {profile.requirements.length === 0 ? (
            <p className="text-sm text-text-muted">
              No role requirements set for {profile.role_title || "this role"} yet.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {profile.requirements.map((r) => (
                <li
                  key={r.skill_id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div>
                    <p className="text-sm text-text-primary">
                      {r.skill_name}{" "}
                      {r.is_critical && (
                        <Badge variant="danger" className="ml-1">
                          critical
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-text-muted">
                      Need {r.required_level} · Has {r.current_level || 0}
                    </p>
                  </div>
                  {r.gap > 0 ? (
                    <Badge variant="warning">−{r.gap}</Badge>
                  ) : (
                    <Badge variant="success">Met</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            Skills ({Object.keys(draft).length})
          </h3>
          <Button size="sm" variant="secondary" onClick={() => setShowAddSkill(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add skill
          </Button>
        </div>
        {Object.keys(draft).length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No skills tracked yet. Add some manually or click AI Assess to
            get suggestions.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {Object.values(draft)
              .sort((a, b) => a.skill_name.localeCompare(b.skill_name))
              .map((d) => (
                <li
                  key={d.skill_id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div>
                    <p className="text-sm text-text-primary">{d.skill_name}</p>
                    <p className="text-xs text-text-muted capitalize">
                      {d.skill_category}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <LevelStepper
                      value={d.proficiency_level}
                      onChange={(v) => updateLevel(d.skill_id, v)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromDraft(d.skill_id)}
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            Development plans
          </h3>
          <Button size="sm" onClick={() => setPlanOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            New plan
          </Button>
        </div>
        {plans.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No development plans yet.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {plans.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div>
                  <p className="text-sm text-text-primary">
                    {p.skill_name || "Skill"} · target {p.target_level}
                  </p>
                  <p className="text-xs text-text-muted">
                    Current {p.current_level} · status{" "}
                    <span className="capitalize">{p.status}</span>
                    {p.target_date ? ` · due ${p.target_date}` : ""}
                  </p>
                </div>
                {p.status === "completed" && (
                  <Badge variant="success">
                    <CheckCircle2 className="h-3 w-3" /> Done
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {showAddSkill && (
        <AddSkillModal
          library={library}
          excludeIds={Object.keys(draft)}
          onClose={() => setShowAddSkill(false)}
          onPick={(s) => {
            addToDraft(s);
            setShowAddSkill(false);
          }}
          onCreated={(s) => {
            setLibrary((prev) => [...prev, s]);
            addToDraft(s);
            setShowAddSkill(false);
          }}
        />
      )}

      {aiOpen && id && (
        <AIAssessModal
          employeeId={id}
          onClose={() => setAiOpen(false)}
          onApplyAll={(suggestions) => {
            // Map suggestions to library skills (case-insensitive
            // name match). New names create skills via the library
            // first, then apply.
            const byName = new Map(
              library.map((s) => [s.name.toLowerCase(), s]),
            );
            const newSkillsToCreate: AIAssessSuggestion[] = [];
            const next = { ...draft };
            for (const s of suggestions) {
              const found = byName.get(s.skill_name.toLowerCase());
              if (found) {
                next[found.id] = {
                  skill_id: found.id,
                  skill_name: found.name,
                  skill_category: found.category,
                  proficiency_level: s.proficiency_level,
                };
              } else {
                newSkillsToCreate.push(s);
              }
            }
            setDraft(next);
            // Async-create any missing library entries, then add
            // them to the draft. Best-effort — failures surface a
            // toast but don't roll back the in-draft additions.
            if (newSkillsToCreate.length > 0) {
              void Promise.all(
                newSkillsToCreate.map((s) =>
                  createSkill({ name: s.skill_name, category: s.category })
                    .then((skill) => ({ skill, level: s.proficiency_level }))
                    .catch(() => null),
                ),
              ).then((results) => {
                const more = { ...next };
                for (const r of results) {
                  if (r) {
                    more[r.skill.id] = {
                      skill_id: r.skill.id,
                      skill_name: r.skill.name,
                      skill_category: r.skill.category,
                      proficiency_level: r.level,
                    };
                  }
                }
                setDraft(more);
                setLibrary((prev) => [
                  ...prev,
                  ...(results
                    .map((r) => r?.skill)
                    .filter(Boolean) as Skill[]),
                ]);
              });
            }
            setAiOpen(false);
          }}
        />
      )}

      {planOpen && id && (
        <NewPlanModal
          employeeId={id}
          library={library}
          onClose={() => setPlanOpen(false)}
          onCreated={async () => {
            setPlanOpen(false);
            const pl = await listEmployeePlans(id);
            setPlans(pl);
          }}
        />
      )}
    </motion.div>
  );
}


function LevelStepper({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`h-7 w-7 rounded-md text-xs font-medium border transition-colors ${
            value >= n
              ? "bg-accent text-white border-accent"
              : "bg-surface text-text-muted border-border hover:border-accent/50"
          }`}
          title={LEVEL_LABELS[n]}
          aria-label={`Set level to ${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}


function AddSkillModal({
  library,
  excludeIds,
  onClose,
  onPick,
  onCreated,
}: {
  library: Skill[];
  excludeIds: string[];
  onClose: () => void;
  onPick: (s: Skill) => void;
  onCreated: (s: Skill) => void;
}) {
  const [search, setSearch] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const filtered = library.filter(
    (s) =>
      !excludeIds.includes(s.id) &&
      (s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.category.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <Modal isOpen onClose={onClose} title="Add a skill">
      <input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search the library or create new…"
        className="w-full mb-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
      />
      <div className="max-h-72 overflow-y-auto divide-y divide-border border border-border rounded-lg bg-background">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-muted">
            No matches.
            {search.trim() && (
              <button
                onClick={async () => {
                  if (creatingNew) return;
                  setCreatingNew(true);
                  try {
                    const s = await createSkill({
                      name: search.trim(),
                      category: "technical",
                    });
                    onCreated(s);
                  } finally {
                    setCreatingNew(false);
                  }
                }}
                className="block mt-2 mx-auto text-accent text-xs hover:underline"
              >
                Create &ldquo;{search.trim()}&rdquo; (technical)
              </button>
            )}
          </div>
        ) : (
          filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s)}
              className="w-full text-left px-3 py-2 hover:bg-surface flex items-center justify-between"
            >
              <span className="text-sm text-text-primary">{s.name}</span>
              <span className="text-[11px] text-text-muted capitalize">
                {s.category}
              </span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}


function AIAssessModal({
  employeeId,
  onClose,
  onApplyAll,
}: {
  employeeId: string;
  onClose: () => void;
  onApplyAll: (suggestions: AIAssessSuggestion[]) => void;
}) {
  const { addToast } = useToast();
  const [extra, setExtra] = useState("");
  const [running, setRunning] = useState(false);
  const [out, setOut] = useState<AIAssessSuggestion[] | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await aiAssessEmployee(employeeId, extra.trim() || undefined);
      setOut(r.suggestions);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "AI assessment failed.";
      addToast("error", detail);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="AI Assess" size="lg">
      {!out ? (
        <div className="space-y-3">
          <p className="text-sm text-text-muted">
            We'll suggest 6–12 skills with proficiency levels based on the
            employee's role and any context you paste below. You'll review
            before anything is saved.
          </p>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Extra context (optional)
            </label>
            <textarea
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={5}
              placeholder="Recent projects, performance highlights, certifications…"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button onClick={run} isLoading={running}>
              <Sparkles className="h-4 w-4" />
              Run assessment
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-text-muted">
            Review the suggestions below. Clicking <strong>Apply all</strong>{" "}
            adds them to the profile (you still need to click "Save profile"
            to persist).
          </p>
          <ul className="divide-y divide-border border border-border rounded-lg bg-background max-h-72 overflow-y-auto">
            {out.map((s, i) => (
              <li key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-text-primary">
                    {s.skill_name}{" "}
                    <span className="text-[11px] text-text-muted capitalize">
                      ({s.category})
                    </span>
                  </p>
                  {s.rationale && (
                    <p className="text-xs text-text-muted">{s.rationale}</p>
                  )}
                </div>
                <Badge variant="info">Level {s.proficiency_level}</Badge>
              </li>
            ))}
          </ul>
          <div className="flex justify-between gap-3 pt-2">
            <Button variant="ghost" onClick={() => setOut(null)}>
              Re-run
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                Discard
              </Button>
              <Button onClick={() => onApplyAll(out)}>Apply all</Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}


function NewPlanModal({
  employeeId,
  library,
  onClose,
  onCreated,
}: {
  employeeId: string;
  library: Skill[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [skillId, setSkillId] = useState<string>(library[0]?.id || "");
  const [target, setTarget] = useState<ProficiencyLevel>(3);
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (!skillId) {
      addToast("error", "Pick a skill first.");
      return;
    }
    setSubmitting(true);
    try {
      await createDevelopmentPlan({
        employee_id: employeeId,
        skill_id: skillId,
        target_level: target,
        target_date: date || undefined,
        notes: notes.trim() || undefined,
      });
      addToast("success", "Plan created.");
      onCreated();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not create plan.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New development plan">
      <div className="space-y-3">
        <Select
          label="Skill"
          value={skillId}
          onChange={(e) => setSkillId(e.target.value)}
          options={library.map((s) => ({ value: s.id, label: s.name }))}
        />
        <Select
          label="Target level"
          value={String(target)}
          onChange={(e) =>
            setTarget(Number(e.target.value) as ProficiencyLevel)
          }
          options={[
            { value: "1", label: "1 — Beginner" },
            { value: "2", label: "2 — Intermediate" },
            { value: "3", label: "3 — Advanced" },
            { value: "4", label: "4 — Expert" },
          ]}
        />
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Target date (optional)
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={save} isLoading={submitting}>
            Create plan
          </Button>
        </div>
      </div>
    </Modal>
  );
}
